import { PayoutOrderListItemResponse } from '@/models/payout-order/payout-order-list.model';
import { usePayoutOrderListStore } from '@/store/payout-order/payout-order-list.store';
import { showErrorNotificationWithContext } from '@/utils/show-notification.util';
import { payoutOrdersListApiToDto } from '@/mappers/payout-order-list/payout-orders-list-api-to-dto.mapper';
import { usePayoutOrderListFilter } from '@/hooks/payout-order/payout-order-list-filter.composables';
import { storeToRefs } from 'pinia';
import { useMultiWhitelabelListStore } from '@/store/multi-whitelabel-slug-list/multi-whitelabel-slug-list.store';
import { usePayoutOrderListFilterStore } from '@/store/payout-order/payout-order-filter-list.store';
import { useEnvStore } from '@/store/env.store';
import { payoutOrderListFilterDtoToApi } from '@/mappers/payout-order-list/payout-order-list-filter-dto-to-api.mapper';
import { getBrandContext } from '@/utils/get-brand-context.util';
import { delay } from 'lodash';

const IGNORED_ERROR_STATUS_CODES = [403, 401];
const ERROR_NOTIFICATION_DELAY_MS = 100;

export const usePayoutOrderList = () => {
  const payoutOrderListStore = usePayoutOrderListStore();
  const { setPayoutOrderList, setPayoutOrderListLoading, getPayoutOrderListByWhitelabel } = payoutOrderListStore;

  const { multiWhitelabelSlugList } = storeToRefs(useMultiWhitelabelListStore());
  const { WL_SLUG } = storeToRefs(useEnvStore());

  const { getExternalFilterIdMap } = usePayoutOrderListFilter();

  const {
    isFilterSegmentIdListNotEmpty,
    isFilterTagIdListNotEmpty,
    payoutOrderListFilterFormData,
  } = storeToRefs(usePayoutOrderListFilterStore());

  const showBrandErrorNotification = (
    error: unknown,
    whitelabelSlug: string,
    notificationIndex: number,
  ): void => {
    const notificationDelayMs = (notificationIndex + 1) * ERROR_NOTIFICATION_DELAY_MS;

    delay(() => {
      showErrorNotificationWithContext(error, {
        ignoredStatusCodes: IGNORED_ERROR_STATUS_CODES,
        context: getBrandContext(whitelabelSlug),
      });
    }, notificationDelayMs);
  };

  const loadPayoutOrderList = async (): Promise<void> => {
    setPayoutOrderListLoading(true);

    try {
      const { tagIdMap, segmentIdMap } = await getExternalFilterIdMap();
      const whitelabelSlugList = [...multiWhitelabelSlugList.value, WL_SLUG.value];

      const requestResultList = await Promise.allSettled(
        whitelabelSlugList.map((whitelabelSlug: string) => {
          const tagIdList: string[] = tagIdMap.get(whitelabelSlug) ?? [];
          const segmentIdList: string[] = segmentIdMap.get(whitelabelSlug) ?? [];

          if (
            (isFilterTagIdListNotEmpty.value && !tagIdList.length) ||
            (isFilterSegmentIdListNotEmpty.value && !segmentIdList.length)
          ) {
            return Promise.resolve([]);
          }

          return getPayoutOrderListByWhitelabel(
            whitelabelSlug,
            payoutOrderListFilterDtoToApi(payoutOrderListFilterFormData.value, { tagIdList, segmentIdList }),
          );
        }));

      let errorNotificationIndex = 0;
      const payoutOrderResponseList = requestResultList.flatMap(
        (requestResult, index): PayoutOrderListItemResponse[] => {
          if (requestResult.status === 'fulfilled') {
            return requestResult.value;
          }

          showBrandErrorNotification(
            requestResult.reason,
            whitelabelSlugList[index],
            errorNotificationIndex,
          );
          errorNotificationIndex += 1;

          return [];
        },
      );
      const sortedPayoutOrderResponseList: PayoutOrderListItemResponse[] = payoutOrderResponseList.toSorted(
        (firstItem, secondItem) =>
          new Date(firstItem.createdAt).getTime() -
          new Date(secondItem.createdAt).getTime(),
      );

      setPayoutOrderList(sortedPayoutOrderResponseList.map(payoutOrdersListApiToDto));
    } finally {
      setPayoutOrderListLoading(false);
    }
  };

  return {
    loadPayoutOrderList,
  };
};
